/* Shared utilities for the Day Trading suite.
   Colours, helpers and tiny presentational components used by 2+ tabs. */
import { usePrices } from "./_prices";

export const C = {
  green:"#16a34a", gb:"#dcfce7", gt:"#14532d",
  red:"#dc2626",   rb:"#fee2e2", rt:"#7f1d1d",
  amber:"#d97706", ab:"#fef3c7", at:"#78350f",
  blue:"#2563eb",  bb:"#dbeafe", bt:"#1e3a8a",
  indigo:"#4f46e5",ib:"#e0e7ff", it:"#312e81",
  teal:"#0d9488",  tb:"#ccfbf1", tt:"#134e4a",
  purple:"#7c3aed",pb:"#ede9fe", pt:"#4c1d95",
  gray:"#6b7280",  gbg:"#f3f4f6",gt2:"#374151",
  navy:"#0f172a",  mid:"#1e293b",
  muted:"#94a3b8", dim:"#64748b",
};

export function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export const inputStyle = { padding:"7px 9px", borderRadius:6, border:"0.5px solid #d1d5db", fontSize:12, fontFamily:"inherit", background:"#fff", boxSizing:"border-box", width:"100%" };

export function Badge({ label, bg, text, size=11 }) {
  return <span style={{ fontSize:size, fontWeight:600, padding:"2px 8px", borderRadius:4, background:bg, color:text, whiteSpace:"nowrap", flexShrink:0 }}>{label}</span>;
}

export function Pill({ label, bg, text, size=10 }) {
  return <span style={{ fontSize:size, fontWeight:500, padding:"2px 10px", borderRadius:20, background:bg, color:text, whiteSpace:"nowrap", flexShrink:0 }}>{label}</span>;
}

export function Chip({ label, color }) {
  return <span style={{ fontSize:10, padding:"1px 7px", borderRadius:10, background:color+"18", color, fontWeight:600 }}>{label}</span>;
}

export function Spark({ data, color="#16a34a", w=70, h=24 }) {
  const d = (data||[]).filter(Boolean);
  if (d.length < 2) return <div style={{ width:w, height:h }}/>;
  const mn=Math.min(...d), mx=Math.max(...d), rng=mx-mn||0.01;
  const pts=d.map((v,i)=>`${(i/(d.length-1))*w},${h-1-((v-mn)/rng)*(h-4)}`).join(" ");
  return <svg width={w} height={h} style={{ display:"block", flexShrink:0 }}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

export function Donut({ pct, color, size=48, label="" }) {
  const r=17, circ=2*Math.PI*r;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
      <svg width={size} height={size} viewBox="0 0 48 48">
        <circle cx="24" cy="24" r={r} fill="none" stroke="#e5e7eb" strokeWidth="5"/>
        <circle cx="24" cy="24" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={`${circ*Math.min(pct,100)/100} ${circ}`} strokeLinecap="round" transform="rotate(-90 24 24)"/>
        <text x="24" y="28" textAnchor="middle" fontSize="10" fontWeight="700" fill={color}>{Math.round(pct)}%</text>
      </svg>
      {label && <span style={{ fontSize:10, color:C.gray }}>{label}</span>}
    </div>
  );
}

export function RiskBar({ level }) {
  const map = { Low:{c:C.green,pct:20}, Medium:{c:C.amber,pct:50}, High:{c:C.red,pct:75}, "Very High":{c:C.purple,pct:100} };
  const { c, pct } = map[level]||map.Medium;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:4, background:"#f1f5f9", borderRadius:3, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, background:c, borderRadius:3 }}/>
      </div>
      <span style={{ fontSize:10, fontWeight:700, color:c, width:60, textAlign:"right" }}>{level} Risk</span>
    </div>
  );
}

export function LiveChip({ ticker, showSpark=false }) {
  const { prices } = usePrices();
  const d = prices[ticker];
  if (!d?.ok) return <span style={{ fontSize:11, color:C.muted, background:C.gbg, padding:"2px 8px", borderRadius:20 }}>{ticker} —</span>;
  const up = d.chg >= 0;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20,
      background: up?C.gb:C.rb, border:`1px solid ${up?"#86efac":"#fca5a5"}`, flexShrink:0 }}>
      <b style={{ fontSize:11, color:"#111" }}>{ticker}</b>
      <b style={{ fontSize:12, color:up?C.green:C.red }}>${d.price.toFixed(2)}</b>
      <span style={{ fontSize:10, color:up?C.gt:C.rt }}>{up?"▲":"▼"}{Math.abs(d.chgPct).toFixed(2)}%</span>
      {showSpark && <Spark data={d.closes} color={up?C.green:C.red} w={40} h={18}/>}
    </span>
  );
}

export function Stat({ label, value, color }) {
  return (
    <div style={{padding:"10px 12px",background:"#f9fafb",borderRadius:7}}>
      <div style={{fontSize:10,color:C.gray,textTransform:"uppercase",letterSpacing:"0.04em"}}>{label}</div>
      <div style={{fontSize:16,fontWeight:800,color:color||"#111",marginTop:3}}>{value}</div>
    </div>
  );
}

// ── Safe number formatting helpers ─────────────────────────
// Never throw, never propagate an error. Always return a string.
// Use these instead of bare `.toFixed()` on any value derived from
// network responses (Yahoo, TrueLayer, etc.).
export function fmtNum(n, digits = 2, fallback = "—") {
  if (n === null || n === undefined) return fallback;
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return fallback;
  try { return num.toFixed(digits); } catch { return fallback; }
}

// Safe percentage: e.g. fmtPct(1.2345) → "1.23%" or fmtPct(undefined) → "—"
export function fmtPct(n, digits = 2, fallback = "—") {
  const s = fmtNum(n, digits, null);
  return s === null ? fallback : `${s}%`;
}

// Returns true only if `n` is a real, finite number.
export function isNum(n) {
  return typeof n === "number" && Number.isFinite(n);
}
